import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { userApi, calendarApi, exploreApi, venueApi, equipmentApi } from '../lib/api';
import { 
  Search, ArrowRight, Flame, Users, Calendar as CalendarIcon, Shield, 
  Music, Palette, Camera, Mic, Dumbbell, Sparkles, CalendarDays,
  MapPin, Clock, Crown, RefreshCw, Lock, LogIn, Zap, CheckCircle, X, Building2, Package,
  DollarSign, Star, Percent
} from 'lucide-react';
import { format } from 'date-fns';

// Vibrant, colorful hero images showcasing diverse creative talent
const HERO_IMAGES = [
  { url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', alt: 'Guitarist performing on stage', category: 'Music' },
  { url: 'https://images.pexels.com/photos/8412316/pexels-photo-8412316.jpeg', alt: 'Podcasters in professional studio', category: 'Podcast' },
  { url: 'https://images.pexels.com/photos/5716306/pexels-photo-5716306.jpeg', alt: 'Artist painting on canvas', category: 'Art' },
  { url: 'https://images.pexels.com/photos/1121110/pexels-photo-1121110.jpeg', alt: 'Makeup artist cosmetics', category: 'Beauty' },
  { url: 'https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg', alt: 'Videographer with camera', category: 'Video' },
];

// Placeholder talent images for when no real bookees exist
const PLACEHOLDER_TALENTS = [
  { name: 'DJ Alex', category: 'DJ', image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg', rating: 4.9 },
  { name: 'Sarah Arts', category: 'Painter', image: 'https://images.pexels.com/photos/3785147/pexels-photo-3785147.jpeg', rating: 4.8 },
  { name: 'Lea Lens', category: 'Photographer', image: 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg', rating: 5.0 },
  { name: 'Luna Voice', category: 'Singer', image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', rating: 4.7 },
  { name: 'Fit Coach Jay', category: 'Trainer', image: 'https://images.pexels.com/photos/6455906/pexels-photo-6455906.jpeg', rating: 4.9 },
  { name: 'Maya Model', category: 'Model', image: 'https://images.pexels.com/photos/1926769/pexels-photo-1926769.jpeg', rating: 4.8 },
  { name: 'Beat Master', category: 'DJ', image: 'https://images.pexels.com/photos/2111015/pexels-photo-2111015.jpeg', rating: 4.6 },
  { name: 'Canvas Pro', category: 'Artist', image: 'https://images.pexels.com/photos/5716306/pexels-photo-5716306.jpeg', rating: 4.9 },
  { name: 'Studio Star', category: 'Podcaster', image: 'https://images.pexels.com/photos/8412316/pexels-photo-8412316.jpeg', rating: 4.7 },
  { name: 'Glam Queen', category: 'Makeup', image: 'https://images.pexels.com/photos/1121110/pexels-photo-1121110.jpeg', rating: 5.0 },
];

// Colorful category icons with vibrant gradients
const FEATURED_CATEGORIES = [
  { name: 'DJ', icon: Music, color: 'bg-gradient-to-br from-violet-500/30 to-purple-600/30 text-violet-300 border border-violet-500/40' },
  { name: 'Venues', icon: Building2, color: 'bg-gradient-to-br from-pink-500/30 to-rose-600/30 text-pink-300 border border-pink-500/40', link: '/venues' },
  { name: 'Photographer', icon: Camera, color: 'bg-gradient-to-br from-cyan-500/30 to-blue-600/30 text-cyan-300 border border-cyan-500/40' },
  { name: 'Singer', icon: Mic, color: 'bg-gradient-to-br from-emerald-500/30 to-green-600/30 text-emerald-300 border border-emerald-500/40' },
  { name: 'Personal Trainer', icon: Dumbbell, color: 'bg-gradient-to-br from-orange-500/30 to-amber-600/30 text-orange-300 border border-orange-500/40' },
  { name: 'Equipment Rentals', icon: Package, color: 'bg-gradient-to-br from-teal-500/30 to-cyan-600/30 text-teal-300 border border-teal-500/40', desc: 'Props, lighting, chairs, decor, novelty items & more' },
];

const Landing = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [featuredTalents, setFeaturedTalents] = useState([]);
  const [featuredCalendar, setFeaturedCalendar] = useState({ featured_events: [], featured_bookees: [], artist_of_week: null });
  const [featuredVenues, setFeaturedVenues] = useState([]);
  const [featuredEquipment, setFeaturedEquipment] = useState([]);
  const [exploreBookees, setExploreBookees] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingExplore, setRefreshingExplore] = useState(false);
  const [showBoostBanner, setShowBoostBanner] = useState(true);
  const [categories, setCategories] = useState([]);

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await userApi.getCategories({ sort: 'asc' });
        const sortedCategories = (response.data || []).sort((a, b) => {
          // Keep "Other" at the end
          if (a === 'Other') return 1;
          if (b === 'Other') return -1;
          return a.localeCompare(b);
        });
        setCategories(sortedCategories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Fallback to empty array - categories dropdown will show placeholder
      }
    };
    loadCategories();
  }, []);
  
  // Load recently viewed from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('bookd_recently_viewed');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentlyViewed(parsed.slice(0, 6)); // Show max 6
      } catch (e) {
        console.error('Failed to parse recently viewed:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [talentsRes, featuredRes, exploreRes, venuesRes, equipmentRes] = await Promise.all([
        userApi.getFeaturedTalents().catch(() => ({ data: [] })),
        calendarApi.getFeatured().catch(() => ({ data: { featured_events: [], featured_bookees: [], artist_of_week: null } })),
        exploreApi.getRandom(10).catch(() => ({ data: { bookees: [] } })),
        venueApi.getFeatured(6).catch(() => ({ data: { venues: [] } })),
        equipmentApi.getFeatured(6).catch(() => ({ data: { providers: [] } }))
      ]);
      setFeaturedTalents(talentsRes.data || []);
      setFeaturedCalendar(featuredRes.data);
      setExploreBookees(exploreRes.data.bookees || []);
      setFeaturedVenues(venuesRes.data.venues || []);
      setFeaturedEquipment(equipmentRes.data.providers || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (searchCategory && searchCategory !== 'all') params.set('category', searchCategory);
    navigate(`/browse?${params.toString()}`);
  };

  const handleRefreshExplore = async () => {
    setRefreshingExplore(true);
    try {
      const response = await exploreApi.refresh(10);
      setExploreBookees(response.data.bookees || []);
    } catch (error) {
      console.error('Failed to refresh explore:', error);
    } finally {
      setRefreshingExplore(false);
    }
  };

  // Get display talents - use real bookees or placeholders
  const getDisplayTalents = () => {
    // Filter out test users and users without proper images
    const realBookees = exploreBookees.filter(b => {
      const name = (b.name || '').toLowerCase();
      const screenName = (b.screen_name || '').toLowerCase();
      const email = (b.email || '').toLowerCase();
      
      // Exclude any test/e2e/demo users
      const isTest = name.includes('test') || screenName.includes('test') || 
                     name.includes('e2e') || screenName.includes('e2e') ||
                     name.includes('demo') || screenName.includes('demo') ||
                     name.includes('recipient') || screenName.includes('recipient') ||
                     name.includes('calendar') || screenName.includes('calendar') ||
                     email.includes('test') || email.includes('e2e');
      
      // Must have a portfolio image to be displayed
      const hasImage = b.portfolio_images && b.portfolio_images.length > 0 && b.portfolio_images[0];
      
      return b.name && !isTest && hasImage;
    });
    
    // Always fill to 10 with placeholders to ensure no blanks
    const needed = 10 - realBookees.length;
    const placeholders = PLACEHOLDER_TALENTS.slice(0, Math.max(0, needed)).map((p, idx) => ({
      user_id: `placeholder_${idx}`,
      name: p.name,
      screen_name: p.name,
      categories: [p.category],
      rating: p.rating,
      portfolio_images: [p.image],
      isPlaceholder: true
    }));
    
    // Return real bookees first, then placeholders to fill remaining slots
    return [...realBookees.slice(0, 10), ...placeholders].slice(0, 10);
  };

  // ==================== PRE-LOGIN VIEW ====================
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="hero-bg relative min-h-screen flex items-center pt-16">
          <div className="hero-glow absolute inset-0" />
          
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
            <div className="max-w-3xl mx-auto lg:mx-0">
              <div className="space-y-8 animate-fade-in-up">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Book Creatives & Talent Instantly</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Find & Book
                  <span className="block bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">Talent & Creatives</span>
                  For Any Event
                </h1>
                
                <p className="text-base md:text-lg text-muted-foreground max-w-xl">
                  Connect with DJs, musicians, photographers, models, personal trainers, and 50+ creative categories. 
                  Book hourly or flat rate, manage schedules, and create contracts seamlessly.
                </p>

                {/* Hero Images */}
                <div className="grid grid-cols-6 gap-3 md:gap-4">
                  <div className="col-span-3 relative rounded-2xl overflow-hidden h-32 sm:h-40 lg:h-44 shadow-xl ring-1 ring-white/10">
                    <img src={HERO_IMAGES[0].url} alt={HERO_IMAGES[0].alt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                  <div className="col-span-3 relative rounded-2xl overflow-hidden h-32 sm:h-40 lg:h-44 shadow-xl ring-1 ring-white/10">
                    <img src={HERO_IMAGES[1].url} alt={HERO_IMAGES[1].alt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                  <div className="col-span-2 relative rounded-2xl overflow-hidden h-28 sm:h-32 lg:h-36 shadow-xl ring-1 ring-white/10">
                    <img src={HERO_IMAGES[2].url} alt={HERO_IMAGES[2].alt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                  <div className="col-span-2 relative rounded-2xl overflow-hidden h-28 sm:h-32 lg:h-36 shadow-xl ring-1 ring-white/10">
                    <img src={HERO_IMAGES[3].url} alt={HERO_IMAGES[3].alt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                  <div className="col-span-2 relative rounded-2xl overflow-hidden h-28 sm:h-32 lg:h-36 shadow-xl ring-1 ring-white/10">
                    <img src={HERO_IMAGES[4].url} alt={HERO_IMAGES[4].alt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                </div>

                {/* Stats */}
                <div className="flex gap-8 pt-4">
                  <div>
                    <p className="text-3xl font-bold text-primary">500+</p>
                    <p className="text-sm text-muted-foreground">Active Bookees</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-primary">50+</p>
                    <p className="text-sm text-muted-foreground">Categories</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-primary">24/7</p>
                    <p className="text-sm text-muted-foreground">Marketplace Access</p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg" 
                    className="rounded-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                    onClick={() => navigate('/register')}
                    data-testid="get-started-btn"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => navigate('/login')}
                    data-testid="sign-in-btn"
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section - Visible but not clickable */}
        <section className="py-16 bg-gradient-to-b from-background via-card/50 to-card">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-3 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent" style={{ fontFamily: 'Syne, sans-serif' }}>
                Popular Categories
              </h2>
              <p className="text-muted-foreground">Find the perfect talent for your event</p>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {FEATURED_CATEGORIES.map((cat) => (
                <div
                  key={cat.name}
                  onClick={() => cat.link ? navigate(cat.link) : navigate(`/browse?category=${encodeURIComponent(cat.name)}`)}
                  className={`p-4 md:p-6 rounded-2xl ${cat.color} text-center cursor-pointer transition-transform hover:scale-105 flex flex-col items-center justify-center min-h-[120px] group relative`}
                  title={cat.desc || cat.name}
                >
                  <cat.icon className="h-6 w-6 md:h-8 md:w-8 mx-auto mb-2 md:mb-3" />
                  <p className="font-medium text-xs md:text-sm text-center leading-tight">{cat.name}</p>
                  {cat.desc && (
                    <p className="text-[10px] text-muted-foreground mt-1 opacity-70 line-clamp-1 max-w-full hidden md:block">
                      {cat.desc}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Bookd Section */}
        <section className="py-20 bg-gradient-to-b from-card to-background">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Why Choose <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Bookd</span>?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                The easiest way to discover, book, and work with creative talent
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, title: 'Verified Talent', desc: 'All bookees are verified for quality and professionalism' },
                { icon: Shield, title: 'Secure Payments', desc: 'Your payments are protected until the service is complete' },
                { icon: CalendarIcon, title: 'Easy Scheduling', desc: 'Book instantly with real-time availability calendars' },
                { icon: Zap, title: 'Booking Insurance', desc: 'Get event insurance at 50% below market rates. Off-platform bookings aren\'t covered.', highlight: true },
              ].map((feature, idx) => (
                <Card key={idx} className={`text-center border-border/50 hover:border-primary/50 transition-colors ${feature.highlight ? 'ring-1 ring-primary/30 bg-primary/5' : ''}`}>
                  <CardContent className="pt-6 pb-5">
                    <div className={`w-12 h-12 rounded-xl ${feature.highlight ? 'bg-gradient-to-br from-primary/30 to-secondary/30' : 'bg-gradient-to-br from-primary/20 to-secondary/20'} flex items-center justify-center mx-auto mb-3`}>
                      <feature.icon className={`h-6 w-6 ${feature.highlight ? 'text-primary' : 'text-primary'}`} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                    {feature.highlight && (
                      <Badge variant="outline" className="mt-3 text-xs border-primary/50 text-primary">
                        Exclusive Benefit
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Sign Up CTA */}
        <section className="py-12 bg-gradient-to-b from-background to-card">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 text-primary mb-4">
              <Lock className="h-4 w-4" />
              <span className="text-sm font-medium">Create an account to access all features</span>
            </div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Sign up to browse Bookees, message, and book services
            </h2>
            <div className="flex justify-center gap-4">
              <Button onClick={() => navigate('/register')} className="rounded-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90">
                Create Account
              </Button>
              <Button variant="outline" onClick={() => navigate('/login')} className="rounded-full">
                Sign In
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ==================== POST-LOGIN VIEW ====================
  // Helper to check if user is a Bookee (talent)
  const isBookee = user?.roles?.includes('bookee') || user?.user_type === 'talent' || user?.user_type === 'both';
  const isBookerOnly = user?.user_type === 'client' && !user?.roles?.includes('bookee');
  
  const displayTalents = getDisplayTalents();

  return (
    <div className="min-h-screen pt-16">
      {/* Boost Banner - For Bookees only */}
      {isBookee && showBoostBanner && (
        <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-rose-500/20 border-b border-amber-500/30" data-testid="boost-banner">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-full">
                <Zap className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-amber-300">
                  Boost Your Profile & Get More Bookings
                </p>
                <p className="text-sm text-amber-400/80 hidden sm:block">
                  Featured in Explore • Public Calendar Spotlight • Venue Discounts • Top Search Results
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                size="sm" 
                className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => navigate('/settings?tab=boost')}
                data-testid="boost-banner-btn"
              >
                Boost Profile
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <button 
                onClick={() => setShowBoostBanner(false)}
                className="p-1 hover:bg-white/10 rounded-full"
                data-testid="boost-banner-close"
              >
                <X className="h-4 w-4 text-amber-400/60" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Banner - For Bookees (shows below Boost banner) */}
      {isBookee && showBoostBanner && (
        <div className="bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-fuchsia-500/20 border-b border-violet-500/30" data-testid="bookee-subscribe-banner">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-full">
                <Crown className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <p className="font-medium text-violet-300">
                  Go Pro - Unlock Premium Features
                </p>
                <p className="text-sm text-violet-400/80 hidden sm:block">
                  Priority Support • Advanced Analytics • Verified Badge • More Visibility
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                size="sm" 
                className="rounded-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white"
                onClick={() => navigate('/subscriptions')}
                data-testid="bookee-subscribe-btn"
              >
                View Plans
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Subscribe Banner - For Bookers only */}
      {isBookerOnly && showBoostBanner && (
        <div className="bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-violet-500/20 border-b border-cyan-500/30" data-testid="booker-subscribe-banner">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-full">
                <Sparkles className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="font-medium text-cyan-300">
                  Subscribe & Save on Your Events
                </p>
                <p className="text-sm text-cyan-400/80 hidden sm:block">
                  Discounts on Venues, Vendors, Rentals, and more
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                size="sm" 
                className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                onClick={() => navigate('/subscriptions')}
                data-testid="booker-subscribe-btn"
              >
                View Plans
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <button 
                onClick={() => setShowBoostBanner(false)}
                className="p-1 hover:bg-white/10 rounded-full"
                data-testid="booker-subscribe-close"
              >
                <X className="h-4 w-4 text-cyan-400/60" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content for Logged In Users */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        
        {/* Welcome Message */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-muted-foreground">
            {user?.roles?.includes('bookee') 
              ? "Ready to get discovered? Check your bookings and opportunities."
              : "Find the perfect talent for your next project."
            }
          </p>
        </div>
        
        {/* 1. EXPLORE SECTION - Featured Talent Grid (5x2 desktop, 3x2 mobile) */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm mb-2">
                <Sparkles className="h-4 w-4" />
                Explore
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Featured Talent
              </h2>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefreshExplore}
              disabled={refreshingExplore}
              className="rounded-full"
              data-testid="refresh-explore-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshingExplore ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* 5x2 Grid Desktop, 3x2 Mobile */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {displayTalents.map((talent, idx) => (
              <Card 
                key={talent.user_id || idx}
                className={`overflow-hidden transition-all hover:scale-[1.02] ${talent.isPlaceholder ? 'cursor-default opacity-90' : 'cursor-pointer hover:border-primary/50'} ${idx >= 6 ? 'hidden md:block' : ''}`}
                onClick={() => !talent.isPlaceholder && navigate(`/talent/${talent.user_id}`)}
                data-testid={`explore-card-${idx}`}
              >
                <div className="aspect-square relative">
                  <img 
                    src={talent.portfolio_images?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(talent.screen_name || talent.name)}&background=7c3aed&color=fff&size=200`} 
                    alt={talent.screen_name || talent.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="font-medium text-white text-sm truncate">{talent.screen_name || talent.name}</p>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-xs bg-black/50 backdrop-blur-sm">
                        {talent.categories?.[0] || 'Talent'}
                      </Badge>
                      {talent.rating > 0 && (
                        <span className="flex items-center text-xs text-orange-400">
                          <Flame className="h-3 w-3 fill-current mr-0.5" />
                          {talent.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  {talent.isPlaceholder && (
                    <div className="absolute top-2 right-2">
                      <Badge className="text-xs bg-primary/80">Sample</Badge>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* 2. SEARCH SECTION */}
        <section className="mb-12">
          <Card className="p-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Find Your Perfect Talent
            </h3>
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
              {/* Category Dropdown */}
              <Select value={searchCategory} onValueChange={setSearchCategory}>
                <SelectTrigger className="h-12 w-full md:w-48 rounded-xl bg-background" data-testid="main-category-dropdown">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Try 'wedding DJ', 'birthday photographer', or 'fitness trainer'..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-background"
                  data-testid="main-search-input"
                />
              </div>
              <Button type="submit" size="lg" className="h-12 px-6 rounded-xl bg-gradient-to-r from-primary to-secondary" data-testid="main-search-btn">
                <Search className="h-5 w-5 mr-2" />
                Search
              </Button>
            </form>
            
            {/* Trending Searches */}
            <div className="flex flex-wrap items-center gap-2 mt-4 px-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Flame className="h-3 w-3 text-orange-500" />
                Trending:
              </span>
              {['DJ', 'Photographer', 'Makeup Artist', 'Personal Trainer', 'Singer', 'Videographer'].map((term) => (
                <button
                  key={term}
                  onClick={() => navigate(`/browse?q=${encodeURIComponent(term)}`)}
                  className="text-xs px-3 py-1 rounded-full bg-muted/50 hover:bg-muted border border-border/50 hover:border-primary/30 transition-colors"
                  data-testid={`trending-${term.toLowerCase().replace(' ', '-')}`}
                >
                  {term}
                </button>
              ))}
            </div>
          </Card>
        </section>

        {/* RECENTLY VIEWED SECTION */}
        {recentlyViewed.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm mb-2">
                  <Clock className="h-4 w-4" />
                  Recently Viewed
                </div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Pick Up Where You Left Off
                </h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  localStorage.removeItem('bookd_recently_viewed');
                  setRecentlyViewed([]);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear History
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {recentlyViewed.map((item) => (
                <Card 
                  key={item.id}
                  className="border-border/50 overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => navigate(item.type === 'venue' ? `/venues/${item.id}` : `/talent/${item.screenName || item.id}`)}
                  data-testid={`recently-viewed-${item.id}`}
                >
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                        <Users className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                    {item.type === 'venue' && (
                      <Badge className="absolute top-2 left-2 bg-pink-500/80 text-white text-xs">
                        <Building2 className="h-3 w-3 mr-1" />
                        Venue
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-sm line-clamp-1">{item.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {item.category || item.type}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* 3. CALENDAR SECTION - Always show calendar widget */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-sm mb-2">
                <CalendarDays className="h-4 w-4" />
                Featured Events
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Public Calendar
              </h2>
            </div>
          </div>

          <Card className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Calendar Widget - Always visible */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    {format(new Date(), 'MMMM yyyy')}
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/calendar')}
                    className="rounded-full text-xs"
                  >
                    Select a Day
                  </Button>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-xs font-medium text-muted-foreground py-1">{day}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const today = new Date();
                      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                      const startPadding = firstDay.getDay();
                      const days = [];
                      
                      // Add padding for days before the 1st
                      for (let i = 0; i < startPadding; i++) {
                        days.push(<div key={`pad-${i}`} className="h-8" />);
                      }
                      
                      // Add days of the month
                      for (let d = 1; d <= lastDay.getDate(); d++) {
                        const isToday = d === today.getDate();
                        const hasEvent = featuredCalendar.featured_events?.some(e => {
                          const eventDate = new Date(e.date);
                          return eventDate.getDate() === d && 
                                 eventDate.getMonth() === today.getMonth() &&
                                 eventDate.getFullYear() === today.getFullYear();
                        });
                        
                        days.push(
                          <div 
                            key={d}
                            className={`h-8 w-8 mx-auto flex items-center justify-center rounded-full text-sm cursor-pointer transition-colors
                              ${isToday ? 'bg-primary text-primary-foreground font-bold' : ''}
                              ${hasEvent && !isToday ? 'ring-2 ring-cyan-500 text-cyan-400' : ''}
                              ${!isToday && !hasEvent ? 'hover:bg-muted' : ''}
                            `}
                            onClick={() => navigate('/calendar')}
                          >
                            {d}
                          </div>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>
                </div>
              </div>

              {/* Events Panel */}
              <div>
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Upcoming Events
                </h4>
                
                {featuredCalendar.featured_events?.length > 0 ? (
                  <div className="space-y-3">
                    {featuredCalendar.featured_events.slice(0, 4).map((event, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="h-5 w-5 text-cyan-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{event.title}</p>
                          <p className="text-sm text-muted-foreground">{event.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm">No upcoming events</p>
                    <p className="text-xs text-muted-foreground mt-1">Check back soon for featured events!</p>
                  </div>
                )}

                {/* Artist of the Week */}
                {featuredCalendar.artist_of_week && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      Artist of the Week
                    </h4>
                    <Card 
                      className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => navigate(`/talent/${featuredCalendar.artist_of_week.user_id}`)}
                    >
                      <div className="flex items-center gap-4 p-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={featuredCalendar.artist_of_week.picture} />
                          <AvatarFallback>{featuredCalendar.artist_of_week.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{featuredCalendar.artist_of_week.screen_name || featuredCalendar.artist_of_week.name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Flame className="h-3 w-3 text-orange-500 fill-current" />
                            <span className="text-xs">{featuredCalendar.artist_of_week.rating?.toFixed(1) || '5.0'}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </section>

        {/* FEATURED VENUES SECTION */}
        {isAuthenticated && featuredVenues.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/20 text-pink-400 text-sm mb-2">
                  <Building2 className="h-4 w-4" />
                  Venues
                </div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Featured Venues
                </h2>
                <p className="text-muted-foreground text-sm mt-1">Perfect spaces for your next event</p>
              </div>
              <Link to="/venues">
                <Button variant="outline" className="rounded-full" data-testid="view-all-venues-btn">
                  View All
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredVenues.slice(0, 6).map((venue) => (
                <Card 
                  key={venue.venue_id}
                  className="border-border/50 overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/venues/${venue.venue_id}`)}
                  data-testid={`featured-venue-${venue.venue_id}`}
                >
                  <div className="aspect-video bg-muted relative overflow-hidden">
                    {venue.photos && venue.photos.length > 0 ? (
                      <img 
                        src={venue.photos[0]} 
                        alt={venue.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-500/20 to-purple-500/20">
                        <Building2 className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    {venue.offers_discount && (
                      <Badge className="absolute top-3 right-3 bg-green-500">
                        <Percent className="h-3 w-3 mr-1" />
                        {venue.discount_percent}% Off
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold line-clamp-1">{venue.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {venue.city || venue.address || 'Location TBD'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      {venue.capacity && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          {venue.capacity}
                        </span>
                      )}
                      {venue.hourly_rate && (
                        <span className="text-primary font-medium flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {venue.hourly_rate}/hr
                        </span>
                      )}
                      {venue.rating > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {venue.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* FEATURED EQUIPMENT SECTION */}
        {isAuthenticated && featuredEquipment.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-400 text-sm mb-2">
                  <Package className="h-4 w-4" />
                  Equipment Rentals
                </div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Featured Equipment
                </h2>
                <p className="text-muted-foreground text-sm mt-1">Props, lighting, decor & more for your event</p>
              </div>
              <Link to="/browse?category=Equipment%20Rentals">
                <Button variant="outline" className="rounded-full" data-testid="view-all-equipment-btn">
                  View All
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {featuredEquipment.slice(0, 6).map((provider) => (
                <Card 
                  key={provider.user_id}
                  className="border-border/50 overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/talent/${provider.screen_name || provider.user_id}`)}
                  data-testid={`featured-equipment-${provider.user_id}`}
                >
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    {provider.picture ? (
                      <img 
                        src={provider.picture} 
                        alt={provider.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-500/20 to-cyan-500/20">
                        <Package className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                    {provider.rating > 0 && (
                      <Badge className="absolute top-2 right-2 bg-black/60 text-white text-xs">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400 mr-1" />
                        {provider.rating.toFixed(1)}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-sm line-clamp-1">{provider.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {provider.main_category || 'Equipment Rentals'}
                    </p>
                    {provider.location && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {provider.location}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* 4. BROWSE BY CATEGORY - 6 Colorful Tiles */}
        <section className="mb-12">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/20 text-pink-400 text-sm mb-2">
              <Palette className="h-4 w-4" />
              Categories
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Browse by Category
            </h2>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {FEATURED_CATEGORIES.map((cat) => (
              <div
                key={cat.name}
                onClick={() => cat.link ? navigate(cat.link) : navigate(`/browse?category=${encodeURIComponent(cat.name)}`)}
                className={`p-4 md:p-6 rounded-2xl ${cat.color} text-center cursor-pointer transition-all hover:scale-105 hover:shadow-lg flex flex-col items-center justify-center min-h-[120px]`}
                data-testid={`category-${cat.name.toLowerCase().replace(' ', '-')}`}
                title={cat.desc || cat.name}
              >
                <cat.icon className="h-6 w-6 md:h-8 md:w-8 mx-auto mb-2 md:mb-3" />
                <p className="font-medium text-xs md:text-sm text-center leading-tight">{cat.name}</p>
                {cat.desc && (
                  <p className="text-[10px] text-muted-foreground mt-1 opacity-70 line-clamp-1 max-w-full hidden md:block">
                    {cat.desc}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default Landing;
